import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  getEmpLeaveCalendar,
  syncEmployeeLeaves,
} from "../../../core/data/redux/leaveSlice";
import { formatCalendarEventDescription } from "./LeaveManagementTeamCalendar";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export type LeaveEventType = "LEAVE" | "GH" | "RH" | "SENT" | "NON_WORKING";

export interface ParsedCalendarEvent {
  id: string;
  cleanTitle: string;
  rawDescription: string;
  category: LeaveEventType;
  startDate: Date;
  endDate: Date;
  durationDays: number;
}

function parseToDate(val: any): Date | null {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val === "number") return new Date(val);

  const str = String(val).trim();
  const odataMatch = str.match(/\/Date\((\d+)\)\//);
  if (odataMatch) {
    return new Date(parseInt(odataMatch[1], 10));
  }
  const digitsMatch = str.match(/^\d+$/);
  if (digitsMatch) {
    return new Date(parseInt(str, 10));
  }
  const d = new Date(str);
  return !isNaN(d.getTime()) ? d : null;
}

function formatDateToReadable(date: Date): string {
  if (!date || isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function determineCategory(desc: string, evtType?: string): LeaveEventType {
  const d = (desc || "").toLowerCase();
  const e = (evtType || "").trim();

  // Holiday (GH / RH)
  if (
    e === "06" ||
    d.includes("-gh") ||
    d.includes("gazetted") ||
    d.includes("-nh") ||
    d.includes("national") ||
    d.includes("gandhi") ||
    d.includes("independence") ||
    d.includes("republic")
  ) {
    if (d.includes("-rh") || d.includes("restricted")) return "RH";
    return "GH";
  }
  if (d.includes("-rh") || d.includes("restricted")) {
    return "RH";
  }

  // Sent / Pending Leave
  if (d.includes("sent") || d.includes("pending") || d.includes("requested")) {
    return "SENT";
  }

  // Approved Leave
  if (
    e === "01" ||
    d.includes("leave") ||
    d.includes("casual") ||
    d.includes("earned") ||
    d.includes("absent") ||
    d.includes("quarantine") ||
    d.includes("paternity") ||
    d.includes("maternity")
  ) {
    return "LEAVE";
  }

  // Non-Working Day
  if (e === "00" || d.includes("non-working") || d.includes("non working")) {
    return "NON_WORKING";
  }

  return "LEAVE";
}

const LeaveManagementCalendar: React.FC = () => {
  const dispatch = useDispatch();
  const routes = all_routes;
  const { leaveCalendar, loading } = useSelector((state: any) => state.leavesState || {});

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number>(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  // Read logged-in user empId
  const userJson = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw && raw !== "undefined" ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);
  const empId: string = userJson?.empId || "";

  // Fetch Leave Calendar for the selected year
  const fetchCalendar = useCallback(() => {
    if (!empId) return;
    const formattedEmpId = String(empId).trim().padStart(8, "0");
    dispatch(
      getEmpLeaveCalendar({
        empId: formattedEmpId,
        from: `${selectedYear}-01-01`,
        to: `${selectedYear}-12-31`,
      }) as any
    );
  }, [dispatch, empId, selectedYear]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  // Sync leave data with SAP Info
  const handleSyncSap = async () => {
    if (!empId) return;
    try {
      setIsSyncing(true);
      toast.info("Syncing leave data with SAP...");
      const formattedEmpId = String(empId).trim().padStart(8, "0");
      await dispatch(syncEmployeeLeaves({ empId: formattedEmpId }) as any).unwrap();
      toast.success("Leave data synced successfully!");
      fetchCalendar();
    } catch (error: any) {
      console.error("Failed to sync leaves", error);
      toast.error(error || "Failed to sync SAP data.");
    } finally {
      setIsSyncing(false);
    }
  };

  // Parse raw response into structured events
  const allEvents: ParsedCalendarEvent[] = useMemo(() => {
    const rawData: any[] = Array.isArray(leaveCalendar?.data?.data)
      ? leaveCalendar.data.data
      : Array.isArray(leaveCalendar?.data)
        ? leaveCalendar.data
        : Array.isArray(leaveCalendar)
          ? leaveCalendar
          : [];

    if (rawData.length === 0) return [];

    const myEvents = rawData.filter(
      (item: any) => item.IsRequester === true || item.IsRequester === undefined
    );

    const parsedList: ParsedCalendarEvent[] = [];

    myEvents.forEach((item: any, idx: number) => {
      const startDate = parseToDate(item.StartDate);
      if (!startDate) return;
      const endDate = parseToDate(item.EndDate) || startDate;

      const rawDesc = String(item.CalendarEventDescription || "").trim();
      const cleanTitle = formatCalendarEventDescription(rawDesc) || rawDesc || "Leave Event";
      const evtType = String(item.CalendarEventType || "").trim();
      const category = determineCategory(rawDesc, evtType);

      const sMid = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
      const eMid = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();
      const durationDays = Math.max(1, Math.round((eMid - sMid) / (1000 * 60 * 60 * 24)) + 1);

      parsedList.push({
        id: `${item.EmployeeID || "EMP"}-${idx}-${startDate.getTime()}-${endDate.getTime()}`,
        cleanTitle,
        rawDescription: rawDesc,
        category,
        startDate,
        endDate,
        durationDays,
      });
    });

    return parsedList;
  }, [leaveCalendar, empId]);

  // Calendar cells generation for selectedMonthIndex & selectedYear
  const calendarCells = useMemo(() => {
    const firstDayOfWeek = new Date(selectedYear, selectedMonthIndex, 1).getDay(); // 0 = Sun
    const daysInMonth = new Date(selectedYear, selectedMonthIndex + 1, 0).getDate();
    const prevMonthDays = new Date(selectedYear, selectedMonthIndex, 0).getDate();

    const cells: { dateNum: string; isCurrentMonth: boolean }[] = [];

    // Previous month trailing days (e.g. 27, 28, 29, 30 for Oct 2026)
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      cells.push({
        dateNum: String(prevMonthDays - i).padStart(2, "0"),
        isCurrentMonth: false,
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({
        dateNum: String(i).padStart(2, "0"),
        isCurrentMonth: true,
      });
    }

    return { cells, firstDayOfWeek, daysInMonth };
  }, [selectedYear, selectedMonthIndex]);

  // Map: dateNum ("01".."31") -> events for that day
  const dateEventMap = useMemo(() => {
    const map = new Map<string, ParsedCalendarEvent[]>();

    allEvents.forEach((ev) => {
      const cur = new Date(ev.startDate);
      const end = new Date(ev.endDate);
      cur.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      const monthStart = new Date(selectedYear, selectedMonthIndex, 1);
      const monthEnd = new Date(selectedYear, selectedMonthIndex + 1, 0);

      const loopStart = cur < monthStart ? monthStart : cur;
      const loopEnd = end > monthEnd ? monthEnd : end;

      const pointer = new Date(loopStart);
      let guard = 0;
      while (pointer <= loopEnd && guard < 32) {
        const dateNum = String(pointer.getDate()).padStart(2, "0");
        const existing = map.get(dateNum) || [];
        if (!existing.some((e) => e.id === ev.id)) {
          existing.push(ev);
        }
        map.set(dateNum, existing);
        pointer.setDate(pointer.getDate() + 1);
        guard++;
      }
    });

    return map;
  }, [allEvents, selectedYear, selectedMonthIndex]);

  // Forward & Backward Handlers
  const handlePrevMonth = () => {
    setSelectedMonthIndex((prev) => {
      if (prev === 0) {
        setSelectedYear((y) => y - 1);
        return 11;
      }
      return prev - 1;
    });
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setSelectedMonthIndex((prev) => {
      if (prev === 11) {
        setSelectedYear((y) => y + 1);
        return 0;
      }
      return prev + 1;
    });
    setSelectedDate(null);
  };

  const today = new Date();
  const isCurrentMonthNow =
    today.getFullYear() === selectedYear && today.getMonth() === selectedMonthIndex;
  const todayNum = String(today.getDate()).padStart(2, "0");

  // Selected date events for details preview
  const selectedDateEvents = useMemo(() => {
    if (!selectedDate) return [];
    return dateEventMap.get(selectedDate) || [];
  }, [selectedDate, dateEventMap]);

  return (
    <div className="page-wrapper bg-light min-vh-100">
      <style>{`
        /* Navigation & Header Controls */
        .pub-nav-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          color: #334155;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }
        .pub-nav-btn:hover {
          background: #f1f5f9;
          color: #0f172a;
          transform: scale(1.05);
        }

        /* Monthly Calendar Card */
        .pub-calendar-card {
          background: #ffffff;
          border: 1px solid #edf2f7;
          border-radius: 14px;
          padding: 16px 18px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          max-width: 520px;
        }
        .pub-cal-day-row {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
          margin-bottom: 8px;
        }
        .pub-cal-day-label {
          text-align: center;
          font-size: 13px;
          font-weight: 500;
          color: #475569;
          padding: 2px 0;
        }
        .pub-cal-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
        }
        .pub-cal-cell {
          aspect-ratio: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          position: relative;
          cursor: pointer;
        }
        .pub-cal-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 13px;
          font-weight: 500;
          color: #1e293b;
          transition: all 0.2s;
        }
        .pub-cal-circle.is-other-month {
          color: #cbd5e1 !important;
          background-color: transparent !important;
          font-weight: 400;
        }
        .pub-cal-circle.is-marked-day {
          background-color: #c0282c !important;
          color: #ffffff !important;
          font-weight: 700;
          box-shadow: 0 2px 4px rgba(192, 40, 44, 0.25);
        }
        .pub-cal-circle.is-today-text {
          color: #c0282c !important;
          font-weight: 700;
          background-color: transparent !important;
        }
        .pub-cal-circle.is-selected-ring {
          outline: 2px solid #0f172a;
          outline-offset: 2px;
        }

        /* Dots indicator row (matching mobile react-native-calendars) */
        .pub-dot-indicator-row {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 4px;
          margin-top: 2px;
        }
        .pub-event-dot {
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background-color: #c0282c;
          display: inline-block;
          flex-shrink: 0;
        }
      `}</style>

      <div className="content">
        {/* Top Header & Sync SAP Button */}
        <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
          <div className="d-flex align-items-center">
            <Link
              to={routes.LeaveManagement.list || "#"}
              className="text-secondary me-3 text-decoration-none bg-white border d-flex align-items-center justify-content-center shadow-sm"
              style={{ width: "36px", height: "36px", borderRadius: "50%" }}
              title="Back to Leave Applications"
            >
              <i className="ti ti-arrow-left fs-16"></i>
            </Link>
            <div>
              <h5 className="fw-bold mb-0 text-dark">Leave Calendar</h5>
              <nav>
                <ol className="breadcrumb mb-0 small" style={{ fontSize: "11px" }}>
                  <li className="breadcrumb-item">
                    <Link to={routes.employeeDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.LeaveManagement.list}>Leave Management</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Leave Calendar
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm d-flex align-items-center rounded-pill px-3 py-1 shadow-sm bg-white"
              style={{ fontSize: "12px", fontWeight: 600 }}
              onClick={handleSyncSap}
              disabled={isSyncing || loading === "pending"}
              title="Sync latest leave records from SAP"
            >
              <i className={`ti ti-refresh fs-14 me-1 ${isSyncing ? "fa-spin" : ""}`}></i>
              {isSyncing ? "Syncing..." : "Sync SAP Info"}
            </button>
          </div>
        </div>

        {/* Monthly Calendar Card (matching mobile layout) */}
        <div className="pub-calendar-card mb-3">
          {/* Card Title */}
          <h5 className="fw-bold mb-3 text-dark" style={{ fontSize: "16px" }}>
            {MONTH_NAMES[selectedMonthIndex]} {selectedYear}
          </h5>

          {/* Month Navigation Row matching mobile: < Month Year > */}
          <div className="d-flex align-items-center justify-content-center gap-4 mb-3">
            <button
              type="button"
              className="btn p-0 border-0"
              style={{ color: "#c0282c", cursor: "pointer", background: "none" }}
              onClick={handlePrevMonth}
              title="Previous Month"
            >
              <i className="ti ti-chevron-left fs-16 fw-bold"></i>
            </button>
            <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "15px" }}>
              {MONTH_NAMES[selectedMonthIndex]} {selectedYear}
            </h6>
            <button
              type="button"
              className="btn p-0 border-0"
              style={{ color: "#c0282c", cursor: "pointer", background: "none" }}
              onClick={handleNextMonth}
              title="Next Month"
            >
              <i className="ti ti-chevron-right fs-16 fw-bold"></i>
            </button>
          </div>

          {/* Day-of-week header: Sun Mon Tue Wed Thu Fri Sat */}
          <div className="pub-cal-day-row">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
              <div key={i} className="pub-cal-day-label">
                {d}
              </div>
            ))}
          </div>

          {/* Day cells grid */}
          <div className="pub-cal-grid">
            {calendarCells.cells.map((cell, idx) => {
              if (!cell.isCurrentMonth) {
                return (
                  <div key={`prev-${cell.dateNum}-${idx}`} className="pub-cal-cell" style={{ cursor: "default" }}>
                    <div className="pub-cal-circle is-other-month">
                      {parseInt(cell.dateNum, 10)}
                    </div>
                  </div>
                );
              }

              const dateNum = cell.dateNum;
              const eventsOnDate = dateEventMap.get(dateNum) || [];
              const hasEvents = eventsOnDate.length > 0;
              const isToday = isCurrentMonthNow && dateNum === todayNum;
              const isSelected = selectedDate === dateNum;

              // Mobile CalendarView logic:
              // All dates returned by the API (leaveData: holidays, non-working days, leaves)
              // are marked with a solid red circle (selectedColor: COLORS.primary) + a red dot.
              // Today (without scheduled events) displays in red text (todayTextColor: COLORS.primary).
              let circleClass = "";
              if (hasEvents) {
                circleClass = "is-marked-day";
              } else if (isToday) {
                circleClass = "is-today-text";
              }

              return (
                <div
                  key={`date-${dateNum}`}
                  className="pub-cal-cell"
                  onClick={() => {
                    setSelectedDate((prev) => (prev === dateNum ? null : dateNum));
                  }}
                  title={
                    hasEvents
                      ? eventsOnDate.map((e) => e.cleanTitle).join(", ")
                      : undefined
                  }
                >
                  <div
                    className={`pub-cal-circle ${circleClass} ${
                      isSelected ? "is-selected-ring" : ""
                    }`}
                  >
                    {parseInt(dateNum, 10)}
                  </div>

                  {/* Dot indicator row (matching mobile react-native-calendars) */}
                  {hasEvents && (
                    <div className="pub-dot-indicator-row">
                      <span className="pub-event-dot" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend matching mobile */}
          <div className="d-flex align-items-center justify-content-between pt-3 mt-3 border-top flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <span className="d-inline-flex align-items-center gap-1 small text-muted" style={{ fontSize: "11px" }}>
                <span className="pub-event-dot" style={{ width: "8px", height: "8px" }} />
                <span>Scheduled / Off Day</span>
              </span>
              <span className="d-inline-flex align-items-center gap-1 small text-muted" style={{ fontSize: "11px" }}>
                <span className="fw-bold text-danger" style={{ fontSize: "12px", lineHeight: 1 }}>14</span>
                <span>Today</span>
              </span>
            </div>
          </div>
        </div>

        {/* Selected Date Information Banner (if clicked) */}
        {selectedDate && (
          <div
            className="p-3 bg-white rounded-3 border shadow-sm mb-3"
            style={{ maxWidth: "520px" }}
          >
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="fw-bold text-dark fs-13">
                {parseInt(selectedDate, 10)} {MONTH_NAMES[selectedMonthIndex]} {selectedYear}
              </span>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm rounded-pill px-2 py-0 border"
                style={{ fontSize: "11px" }}
                onClick={() => setSelectedDate(null)}
              >
                Clear
              </button>
            </div>

            {selectedDateEvents.length > 0 ? (
              <div className="d-flex flex-column gap-2">
                {selectedDateEvents.map((ev) => (
                  <div key={ev.id} className="p-2 bg-light rounded border fs-12">
                    <span className="fw-semibold text-dark d-block mb-1">{ev.cleanTitle}</span>
                    <span className="text-muted d-block small">
                      {formatDateToReadable(ev.startDate)}
                      {ev.durationDays > 1 ? ` - ${formatDateToReadable(ev.endDate)} · ${ev.durationDays} Days` : ""}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-muted small">No scheduled leaves or holidays on this date.</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveManagementCalendar;
